import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, AlertCircle } from 'lucide-react';
import BottomTabNavigation from '../../components/navigation/BottomTabNavigation';
import subscriptionService from '../../services/subscriptionService';
import UsageCard from './components/UsageCard';
import LimitExplanation from './components/LimitExplanation';
import UpgradeSuggestion from './components/UpgradeSuggestion';
import UsageHistoryChart from './components/UsageHistoryChart';
import ResetNotification from './components/ResetNotification';
import UpgradeCTA from '../../components/UpgradeCTA';
import CloseButton from '../../components/CloseButton';
import LogoutButton from '../../components/LogoutButton';

const UsageLimitsDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [usageData, setUsageData] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    loadUsageData();
  }, []);

  const loadUsageData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Load current subscription
      const { data: subData, error: subError } = await subscriptionService?.getCurrentSubscription();
      if (subError) throw subError;
      setSubscription(subData);

      // Load usage for all limited features
      const exportUsage = await subscriptionService?.getFeatureUsage('monthly_exports');
      const caregiverUsage = await subscriptionService?.getFeatureUsage('caregiver_connections');
      const storageUsage = await subscriptionService?.getFeatureUsage('data_storage');
      const analyticsUsage = await subscriptionService?.getFeatureUsage('advanced_analytics');

      setUsageData({
        monthlyExports: exportUsage,
        caregiverConnections: caregiverUsage,
        dataStorage: storageUsage,
        advancedAnalytics: analyticsUsage
      });
    } catch (err) {
      console.error('Failed to load usage data:', err);
      setError('Failed to load usage information');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = () => {
    navigate('/subscription-management');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading usage data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-md p-6 max-w-md w-full text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Data</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadUsageData}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const isBasicPlan = subscription?.plan?.planType === 'basic';
  const limits = subscription?.plan?.limits || {};

  // Calculate next reset date (first day of next month)
  const getNextResetDate = () => {
    const now = new Date();
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return nextMonth;
  };

  const nextResetDate = getNextResetDate();

  // Check if user is approaching any limits
  const isApproachingLimit = () => {
    const exportUsage = usageData?.monthlyExports?.usageCount || 0;
    const exportLimit = limits?.monthly_exports_limit || 2;
    return exportUsage >= exportLimit * 0.7; // 70% threshold
  };

  // Feature usage data for cards
  const features = [
    {
      name: 'Symptom Exports',
      key: 'monthly_exports',
      usage: usageData?.monthlyExports?.usageCount || 0,
      limit: limits?.monthly_exports_limit || 2,
      unit: 'exports',
      description: 'Export your symptom data to share with doctors',
      tips: [
        'Plan your exports strategically for doctor appointments',
        'Use the preview feature before exporting',
        'Consider upgrading for unlimited exports'
      ]
    },
    {
      name: 'Caregiver Connections',
      key: 'caregiver_management',
      usage: 0,
      limit: limits?.caregiver_connections_limit || 0,
      unit: 'connections',
      description: 'Connect with caregivers to share your health data',
      tips: [
        'Upgrade to Premium to add caregivers',
        'Caregivers can monitor your symptoms in real-time',
        'Set up custom alerts for important events'
      ]
    },
    {
      name: 'Data Storage',
      key: 'data_storage',
      usage: limits?.symptom_history_days || 7,
      limit: 180,
      unit: 'days',
      description: 'How long your symptom history is stored',
      tips: [
        'Basic plan keeps 7 days of history',
        'Premium offers 6 months of data storage',
        'Export important data before it expires'
      ]
    },
    {
      name: 'Advanced Analytics',
      key: 'advanced_analytics',
      usage: 0,
      limit: 0,
      unit: 'access',
      description: 'Detailed insights and trend analysis',
      tips: [
        'Upgrade to access advanced analytics',
        'View symptom trends and patterns',
        'Get predictive insights for better health management'
      ]
    }
  ];

  return (
    <>
      <Helmet>
        <title>Usage Limits - MyBodyToday</title>
      </Helmet>

      <div className="min-h-screen bg-gray-50 pb-24">
        <BottomTabNavigation />
        
        {/* Header with Close Button */}
        <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => navigate('/subscription-management')}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  aria-label="Go back"
                >
                  <ArrowLeft className="w-5 h-5 text-gray-600" />
                </button>
                <TrendingUp className="w-6 h-6 text-indigo-600" />
                <h1 className="text-2xl font-bold text-gray-900">Usage Limits</h1>
              </div>
              <div className="flex items-center gap-3">
                <CloseButton 
                  onClose={() => navigate('/subscription-management')} 
                  navigateTo="/subscription-management" 
                  ariaLabel="Close usage limits" 
                />
                <LogoutButton />
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
          {/* Reset Notification */}
          {isBasicPlan && (
            <ResetNotification nextResetDate={nextResetDate} />
          )}

          {/* Contextual Upgrade CTA - Show if approaching limits */}
          {isBasicPlan && isApproachingLimit() && (
            <UpgradeCTA
              variant="default"
              context="limit-reached"
              featureName="Monthly Exports"
              currentUsage={usageData?.monthlyExports?.usageCount || 0}
              limit={limits?.monthly_exports_limit || 2}
            />
          )}

          {/* Upgrade Banner for Basic Plan */}
          {isBasicPlan && (
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg p-6 text-white">
              <h2 className="text-xl font-bold mb-2">Unlock Unlimited Access</h2>
              <p className="mb-4 opacity-90">
                Upgrade to Premium for unlimited exports, caregiver connections, and advanced analytics.
              </p>
              <button
                onClick={handleUpgrade}
                className="bg-white text-blue-600 px-6 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                View Premium Plans
              </button>
            </div>
          )}

          {/* Usage Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {features?.map((feature) => (
              <UsageCard
                key={feature?.key}
                name={feature?.name}
                usage={feature?.usage}
                limit={feature?.limit}
                unit={feature?.unit}
                description={feature?.description}
                isBasicPlan={isBasicPlan}
              />
            ))}
          </div>

          {/* Limit Explanations */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Understanding Your Limits</h2>
            <div className="space-y-4">
              {features?.map((feature) => (
                <LimitExplanation
                  key={feature?.key}
                  name={feature?.name}
                  description={feature?.description}
                  tips={feature?.tips}
                />
              ))}
            </div>
          </div>

          {/* Usage History Chart */}
          <UsageHistoryChart usageData={usageData} />

          {/* Upgrade Suggestions */}
          {isBasicPlan && (
            <UpgradeSuggestion
              usageData={usageData}
              limits={limits}
              onUpgrade={handleUpgrade}
            />
          )}

          {/* Alternative Workflows */}
          {isBasicPlan && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Maximizing Your Basic Plan
              </h3>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-semibold">
                    1
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Schedule exports strategically</p>
                    <p className="text-sm text-gray-600">
                      Plan your 2 monthly exports around doctor appointments for maximum value
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-semibold">
                    2
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Keep consistent daily check-ins</p>
                    <p className="text-sm text-gray-600">
                      Regular tracking ensures you have quality data when you need to export
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-semibold">
                    3
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Use preview before exporting</p>
                    <p className="text-sm text-gray-600">
                      Review your data in the preview to ensure it's complete before using an export
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default UsageLimitsDashboard;